import React, { FC, useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    Col,
    Collapse,
    Container,
    Form,
    InputGroup,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import {
    useApolloClient,
    useLazyQuery,
    useMutation,
    useQuery,
    useSubscription
} from "@apollo/client";
import { GET_RESULTS, GET_REVISION } from "../../graphql/queries";
import {
    BatchBuild,
    BatchBuildsData,
    BatchBuildState,
    BatchBuildsVariables,
    BuildType,
    OrderMode,
    RevisionData,
    RevisionVariables
} from "../../models/types";
import {
    useLocation,
    useNavigate,
    useNavigationType,
    useParams
} from "react-router-dom";
import { useLocalState } from "../../graphql/hooks";
import { BackButton } from "../simple/BackButton";
import { UPDATE_BUILD } from "../../graphql/mutations";
import { HistoryItem } from "../simple/HistoryItem";
import { BuildLogsModal } from "../modals/BuildLogsModal";
import { Paginator } from "../lists/Paginator";
import { BUILD_HISTORY_CHANGED_SUBSCRIPTION } from "../../graphql/subscriptions";
import { UpButton } from "../buttons/UpButton";
import { BuildSortButton } from "../buttons/BuildSortButton";
import { BuildHistoryDateRangePicker } from "../simple/BuildHistoryDateRangePicker";
import { faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { clearFunnelSelection } from "../../common/AnalyticsHelpers";
import cx from "classnames";
import styles from "./BuildReport.module.scss";

interface Props {
    isFullHistory: boolean;
}

export const History: FC<Props> = ({ isFullHistory }) => {
    const client = useApolloClient();
    const state = useLocalState();
    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();
    const [builds, updateBuilds] = useState<BatchBuild[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [dateRange, setDateRange] = useState({ start: "", end: "" });
    const [showHelp, setShowHelp] = useState(false);
    const toggleShowHelp = () => setShowHelp(!showHelp);
    const [showCompletedOnly, setShowCompletedOnly] = useState(false);
    const [showErrorLogs, updateShowErrorLogs] = useState(false);
    const [sortConfig, setSortConfig] = useState({
        key: "",
        direction: OrderMode.None
    });
    const [errorBatchBuildId, updateErrorBatchBuildId] = useState<
        number | undefined
    >(undefined);
    const { productId, projectId, revisionId } = useParams<{
        productId: string;
        projectId: string;
        revisionId: string;
    }>();
    const [queryBatchBuilds, { data, error }] = useLazyQuery<{
        builds: BatchBuild[];
    }>(GET_RESULTS);
    const { data: { revision } = {} } = useQuery<
        RevisionData,
        RevisionVariables
    >(GET_REVISION, { variables: { revisionId: Number(revisionId) } });

    const [updateBuildStateMutation] = useMutation(UPDATE_BUILD);

    const startSort = sortConfig.key && sortConfig.direction !== OrderMode.None;

    const hasBuildReport = (build: BatchBuild) =>
        build.state >= BatchBuildState.Completed &&
        build.state !== BatchBuildState.Failed &&
        build.state !== BatchBuildState.Stopped;

    const sortedJobs = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        const filteredBuilds = (
            state.developerMode
                ? builds
                : builds.filter(
                      build =>
                          build.buildType !== BuildType.Mutator &&
                          !build.developmentBuild
                  )
        )
            .filter(build => !showCompletedOnly || hasBuildReport(build))
            .filter(build => {
                if (!query) return true;
                return (
                    build.id.toString().includes(query) ||
                    (build.name ?? "").toLowerCase().includes(query) ||
                    (build.user?.displayName ?? "")
                        .toLowerCase()
                        .includes(query)
                );
            })
            .filter(build => {
                const buildDate = new Date(build.dateCreated);
                if (dateRange.start && buildDate < new Date(dateRange.start))
                    return false;
                if (
                    dateRange.end &&
                    buildDate > new Date(dateRange.end + "T23:59:59")
                )
                    return false;
                return true;
            });

        if (!startSort) {
            return filteredBuilds;
        }

        const sortableJobs = [...filteredBuilds];
        sortableJobs.sort((a, b) => {
            const isAscending = sortConfig.direction === OrderMode.Ascending;

            if (
                sortConfig.key === "dateCreated" ||
                sortConfig.key === "dateUpdated"
            ) {
                const dateA = new Date(a[sortConfig.key]).getTime();
                const dateB = new Date(b[sortConfig.key]).getTime();
                return (isAscending ? 1 : -1) * (dateA - dateB);
            }
            if (sortConfig.key === "name") {
                const nameA = (a.name ?? "").toLowerCase();
                const nameB = (b.name ?? "").toLowerCase();
                return (isAscending ? 1 : -1) * nameA.localeCompare(nameB);
            }
            if (sortConfig.key === "user") {
                const userA = (a.user?.displayName ?? "").toLowerCase();
                const userB = (b.user?.displayName ?? "").toLowerCase();
                return (isAscending ? 1 : -1) * userA.localeCompare(userB);
            }
            return 0;
        });
        return sortableJobs;
    }, [
        builds,
        dateRange,
        searchQuery,
        showCompletedOnly,
        sortConfig,
        startSort,
        state.developerMode
    ]);

    const handleSortClick = (criteria: string) => {
        setSortConfig(prevConfig => {
            if (prevConfig.key === criteria) {
                switch (prevConfig.direction) {
                    case OrderMode.Ascending:
                        return {
                            ...prevConfig,
                            direction: OrderMode.Descending
                        };
                    case OrderMode.Descending:
                        return { key: "", direction: OrderMode.None };
                    case OrderMode.None:
                        return {
                            key: criteria,
                            direction: OrderMode.Ascending
                        };
                    default:
                        return prevConfig;
                }
            } else {
                return { key: criteria, direction: OrderMode.Ascending };
            }
        });
    };

    useEffect(() => {
        clearFunnelSelection(client);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (revision) {
            queryBatchBuilds({
                variables: {
                    revisionId: revision.id
                },
                fetchPolicy: "cache-and-network"
            });
        }
    }, [
        revision,
        state.developerMode,
        state.buildNotifications,
        queryBatchBuilds
    ]);

    useEffect(() => {
        if (data && data.builds) {
            const builds = [...data.builds];
            updateBuilds(builds);
        }
    }, [data]);

    useSubscription(BUILD_HISTORY_CHANGED_SUBSCRIPTION, {
        variables: { projectId: projectId },
        onData: ({
            data: { data: { buildHistoryChangedNotification } = {} },
            client
        }) => {
            const currentBuilds = data?.builds;
            if (currentBuilds && buildHistoryChangedNotification) {
                const newBuilds = currentBuilds.map(build => {
                    if (
                        build.id ===
                        buildHistoryChangedNotification.buildId.toString()
                    ) {
                        return {
                            ...build,
                            state: buildHistoryChangedNotification.buildState
                        };
                    } else {
                        if (
                            build.state === BatchBuildState.Review ||
                            build.state === BatchBuildState.Test
                        ) {
                            return {
                                ...build,
                                state: BatchBuildState.Completed
                            };
                        }
                        return build;
                    }
                });
                client.cache.writeQuery<BatchBuildsData, BatchBuildsVariables>({
                    query: GET_RESULTS,
                    data: { builds: newBuilds },
                    variables: { revisionId: revision?.id }
                });
                client.refetchQueries({
                    include: [GET_REVISION]
                });
            }
        }
    });

    const updateBuildState = async (
        buildId: number,
        newState: BatchBuildState
    ) => {
        try {
            await updateBuildStateMutation({
                variables: { id: buildId, state: newState },
                refetchQueries: [
                    {
                        query: GET_RESULTS,
                        variables: { revisionId: revisionId }
                    },
                    {
                        query: GET_REVISION,
                        variables: { revisionId: revisionId }
                    }
                ]
            });
        } catch (error) {
            console.log("[DEBUG] updateBuildState error ", error);
        }
    };

    if (!revision || !data) {
        if (error) {
            return (
                <Container className="fullSize p-2 pt-5 text-center">
                    <h5 className="text-danger">{error.message}</h5>
                </Container>
            );
        }

        return null;
    }

    const findLatestBuild = (builds: BatchBuild[]): number => {
        let latestDate = 0;
        let latestIndex = 0;
        builds.forEach((build, index) => {
            if (
                build.state !== BatchBuildState.Failed &&
                build.state !== BatchBuildState.Started &&
                build.state !== BatchBuildState.Stopped &&
                !build.developmentBuild
            ) {
                const buildDate = new Date(build.dateCreated).getTime();
                if (buildDate > latestDate) {
                    latestDate = buildDate;
                    latestIndex = index;
                }
            }
        });
        return latestIndex;
    };
    const latestBuild = findLatestBuild(sortedJobs);

    return (
        <>
            <Container
                className={`pt-1 ${
                    state.developerMode
                        ? "pageMaxWidthDev"
                        : "pageMaxWidthClient"
                }`}
            >
                {isFullHistory ? (
                    <div>
                        <Row className="m-0 p-0 pt-2 align-items-center">
                            <Col>
                                <BackButton
                                    hasHistory={
                                        !(
                                            location.key &&
                                            navigationType === "POP"
                                        )
                                    }
                                    goBack={() => navigate(-1)}
                                    size={"lg"}
                                />
                            </Col>
                            <Col className="text-end">
                                <OverlayTrigger
                                    placement="bottom"
                                    delay={{ show: 550, hide: 200 }}
                                    overlay={
                                        <Tooltip id="history-customator-tooltip">
                                            Go to Customator with this project
                                        </Tooltip>
                                    }
                                >
                                    <Button
                                        variant="primary"
                                        className="me-2 mt-2"
                                        onClick={() =>
                                            navigate(
                                                `/products/${productId}/projects/${projectId}/revisions/${revisionId}/variations/customator`
                                            )
                                        }
                                    >
                                        Customator
                                    </Button>
                                </OverlayTrigger>
                            </Col>
                        </Row>
                    </div>
                ) : null}
                <Card
                    className={`text-contrast mb-3 ${
                        isFullHistory ? "mt-3 mb-0" : "p-0"
                    }`}
                >
                    <Card.Header>
                        <h5>
                            Build History of{" "}
                            <span className="text-purple">
                                {revision?.name}
                            </span>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 550, hide: 200 }}
                                overlay={
                                    <Tooltip id="history-help-tooltip">
                                        {showHelp ? "Hide Help" : "Show Help"}
                                    </Tooltip>
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    size="sm"
                                    onClick={toggleShowHelp}
                                    className={cx(
                                        styles.infoIcon,
                                        `mouseHover ms-3 ${
                                            showHelp
                                                ? "text-subtle"
                                                : "text-disabled"
                                        }`
                                    )}
                                />
                            </OverlayTrigger>
                        </h5>
                        <Collapse in={showHelp}>
                            <div className="text-subtle analytics-font">
                                List of all generated customator builds for this
                                project.
                                <br />
                                Ongoing build jobs are marked with a progress
                                pipeline.
                                <br />
                                Click a completed build to access the ad files
                                in the build report.
                                <br />
                                Uncompleted builds that failed or were stopped
                                during the build process will not have a build
                                report available.
                            </div>
                        </Collapse>
                    </Card.Header>
                    <Card.Body className="p-2 overflow-x-hidden">
                        {builds.length > 0 && (
                            <>
                                <Row className="mb-2 p-2 align-items-center g-4">
                                    <Col xs={12} md={6} lg={4}>
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search by build name, ID, or user..."
                                                value={searchQuery}
                                                onChange={e =>
                                                    setSearchQuery(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <Button
                                                variant="outline-secondary text-muted"
                                                disabled={!searchQuery}
                                                onClick={() =>
                                                    setSearchQuery("")
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faXmark}
                                                    size="lg"
                                                />
                                            </Button>
                                        </InputGroup>
                                    </Col>
                                    <Col xs="auto">
                                        <OverlayTrigger
                                            placement="top"
                                            delay={{ show: 550, hide: 100 }}
                                            overlay={
                                                <Tooltip id="completed-only-tooltip">
                                                    Show only completed builds
                                                </Tooltip>
                                            }
                                        >
                                            <Form.Check
                                                type="switch"
                                                id="completed-only-switch"
                                                label="Completed only"
                                                checked={showCompletedOnly}
                                                onChange={e =>
                                                    setShowCompletedOnly(
                                                        e.target.checked
                                                    )
                                                }
                                                className="text-subtle bread-text"
                                            />
                                        </OverlayTrigger>
                                    </Col>
                                    <Col xs="auto" className="ms-auto text-end">
                                        <BuildHistoryDateRangePicker
                                            startDate={dateRange.start}
                                            endDate={dateRange.end}
                                            onChange={(start, end) =>
                                                setDateRange({ start, end })
                                            }
                                        />
                                    </Col>
                                </Row>
                                <Row
                                    className={cx(
                                        "align-items-center g-0 mx-2 mb-2",
                                        styles.sortHeaderRow
                                    )}
                                >
                                    <Col className="col-4">
                                        <BuildSortButton
                                            criteria="name"
                                            label="Name"
                                            tooltipText="Sort by build name"
                                            sortDirection={sortConfig.direction}
                                            isSelected={
                                                sortConfig.key === "name"
                                            }
                                            onClick={handleSortClick}
                                        />
                                    </Col>
                                    <Col className="col-2">
                                        <BuildSortButton
                                            criteria="dateCreated"
                                            label="Date"
                                            tooltipText="Sort by last updated"
                                            sortDirection={sortConfig.direction}
                                            isSelected={
                                                sortConfig.key === "dateCreated"
                                            }
                                            onClick={handleSortClick}
                                        />
                                    </Col>
                                    <Col className="col-1 d-flex justify-content-center">
                                        <BuildSortButton
                                            criteria="user"
                                            label="User"
                                            tooltipText="Sort by user"
                                            sortDirection={sortConfig.direction}
                                            isSelected={
                                                sortConfig.key === "user"
                                            }
                                            onClick={handleSortClick}
                                        />
                                    </Col>
                                </Row>
                            </>
                        )}
                        {sortedJobs && sortedJobs.length > 0 ? (
                            <Paginator
                                list={sortedJobs.map(
                                    (build: BatchBuild, index: number) => (
                                        <HistoryItem
                                            key={build.id}
                                            object={build}
                                            developerMode={state.developerMode}
                                            updateBuildState={updateBuildState}
                                            isLatestBuild={
                                                latestBuild === index
                                            }
                                            isClickable={hasBuildReport(build)}
                                            revisionType={revision.releaseType}
                                            linkPath={
                                                build.state !==
                                                BatchBuildState.Started
                                                    ? `/products/${productId}/projects/${projectId}/revisions/${revisionId}/history/${build.id}`
                                                    : "#"
                                            }
                                            openErrorLogs={buildId => {
                                                updateErrorBatchBuildId(
                                                    buildId
                                                );
                                                updateShowErrorLogs(true);
                                            }}
                                        />
                                    )
                                )}
                                maxElementCount={isFullHistory ? 25 : 7}
                                activeItem={undefined}
                            />
                        ) : (
                            <h5 className="text-muted text-center m-3">
                                No build history found.
                            </h5>
                        )}
                    </Card.Body>
                </Card>
                <Row className="m-0 justify-content-center">
                    {isFullHistory && sortedJobs && sortedJobs.length > 20 ? (
                        <span className="mb-3">
                            <UpButton />
                        </span>
                    ) : null}
                </Row>
            </Container>
            {showErrorLogs && errorBatchBuildId && state.developerMode ? (
                <BuildLogsModal
                    show={showErrorLogs}
                    onClose={() => updateShowErrorLogs(false)}
                    job={undefined}
                    id={undefined}
                    targetIndex={undefined}
                    buildReport={undefined}
                    batchBuildId={errorBatchBuildId}
                />
            ) : null}
        </>
    );
};
